import { round } from "lodash";
import defaults from "lodash/defaults";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import { formattedTarget } from "../../../utils/actuals";
import { canLoadAllOrganizationAthletesForTasks } from "../../../utils/organization-permissions-helpers";
import AddEditTaskModal from "./components/add-edit-task-modal";
import View from "./tasks-view";

const TasksController = async ({ res, req, locals, load, momentTZ }) => {
  const organizationRoleName = locals.organizationRoleName;

  const isAllowToLoadAllAthletes = canLoadAllOrganizationAthletesForTasks(
    organizationRoleName
  );
  const { query = {} } = req;

  defaults(query, {
    tab: "programming",
    completed: false,
    modal: {},
    filterGroup:
      isAllowToLoadAllAthletes &&
      query.modal &&
      query.modal.type &&
      (query.modal.type === "edit-task" || query.modal.type === "add-task")
        ? "organization"
        : "all",
  });
  const {
    tab,
    completed,
    modal,
    id,
    filterGroup,
    organizationCoach = undefined,
  } = query;

  const activeUser = locals.activeUser;
  const { organizationId = null } = activeUser;
  const loadPromises = [];
  if (organizationId) {
    loadPromises.push(load("usersOrganization"));
  }

  if (isAllowToLoadAllAthletes) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  if (organizationCoach) {
    loadPromises.push(
      load("coachOrganizationInformation", { user: organizationCoach }),
      load("tasks", { tab, completed, filterGroup, organizationCoach })
    );
  } else {
    loadPromises.push(
      load("tasks", {
        tab,
        completed,
        filterGroup,
        organizationCoach: undefined,
      })
    );
  }

  await Promise.all(loadPromises);

  let taskToEdit = null;
  if (modal.type === "edit-task" && id) {
    taskToEdit = locals.tasks.thisWeek.find((task) => task._id === id);
    if (!taskToEdit) {
      taskToEdit = locals.tasks.upcoming.find((task) => task._id === id);
    }
  }

  if (query.threadId) {
    await load("comments", {
      threadId: query.threadId,
    });
  } else {
    delete locals.comments;
  }

  // Exercise name, protocol (target) and set comment for the video review modal.
  // The task only stores a video pointer, so load the exercise to derive them.
  let videoModalExerciseInfo = {};
  if (modal.type === "exercise-set-video" && query.exerciseToEdit) {
    // Don't let a deleted/inaccessible exercise break the page; the video
    // still plays without the header info.
    try {
      await load("editExercise", {
        exercise: query.exerciseToEdit,
        proxyAthlete: query.athleteId,
        organizationCoach,
      });
    } catch {
      delete locals.editExercise;
    }
    const exercise = locals.editExercise;
    const setIdx = Number(query.setIndex);
    const vidIdx = Number(query.videoIndex);
    const set = exercise?.sets?.[setIdx];
    if (set) {
      const outcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
      const isPowerlifting =
        exercise.type === "primary" || exercise.type === "secondary";
      const { units, intensityMeasure } = locals.activeUser;
      const precision = units === "metric" ? 2 : 1;
      const userMetrics = units === "imperial" ? "lb" : "kg";
      const rawLoad = outcomeObj.load;
      const formattedLoad = rawLoad
        ? `${round(locals.fromMetric(rawLoad, units), precision)} ${userMetrics}`
        : 0;
      const setTarget =
        rawLoad || outcomeObj.reps || outcomeObj.rpe
          ? formattedTarget(
              outcomeObj,
              isPowerlifting,
              set.percentRepMax || undefined,
              units,
              formattedLoad,
              intensityMeasure,
              intensityMeasure !== "rpe",
              true
            ) || undefined
          : undefined;
      videoModalExerciseInfo = {
        primary: exercise.primary,
        secondary: exercise.secondary,
        type: exercise.type,
        movement: exercise.movement,
        setNumber: setIdx + 1,
        setTarget,
        setComment: set.comments?.[vidIdx] || undefined,
      };
    }
  }

  locals.modal = {
    "add-task": (
      <AddEditTaskModal
        tab={tab}
        momentTZ={momentTZ}
        isAllowToLoadAllAthletes={isAllowToLoadAllAthletes}
      />
    ),
    "edit-task": (
      <AddEditTaskModal
        tab={tab}
        momentTZ={momentTZ}
        task={taskToEdit}
        isAllowToLoadAllAthletes={isAllowToLoadAllAthletes}
      />
    ),
    "exercise-set-video": (
      <JWPlayerVideoModal
        mediaId={query.mediaId}
        originalId={query.originalId}
        athleteId={query.uploaderId || query.athleteId}
        locals={locals}
        exerciseToEdit={query.exerciseToEdit}
        reviewedByTheCoach={query.reviewedByTheCoach}
        setIndex={query.setIndex}
        videoIndex={query.videoIndex}
        threadId={query.threadId}
        utcDate={query.utcDate}
        primary={videoModalExerciseInfo.primary}
        secondary={videoModalExerciseInfo.secondary}
        type={videoModalExerciseInfo.type}
        movement={videoModalExerciseInfo.movement}
        setNumber={videoModalExerciseInfo.setNumber}
        setTarget={videoModalExerciseInfo.setTarget}
        setComment={videoModalExerciseInfo.setComment}
      />
    ),
    "activity-feed": <ActivityDrawer />,
  }[modal.type];

  res.body = <View query={query} />;
};

export default TasksController;
